import React, { useState } from 'react';
import { db, collection, getDocs, doc, setDoc } from '../../firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faUpload, 
  faSpinner, 
  faCheckCircle, 
  faExclamationTriangle 
} from '@fortawesome/free-solid-svg-icons';

const DataBackup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchAllCollections = async () => {
    const collections = ['business', 'about', 'hero','services', 'stats', 'reviews', 'gallery', 'contactSubmissions', 'cateringFormSubmissions', 'bccEmails'];
    const backupData = {};

    for (const collectionName of collections) {
      const querySnapshot = await getDocs(collection(db, collectionName));
      backupData[collectionName] = {};
      
      querySnapshot.forEach((doc) => {
        backupData[collectionName][doc.id] = doc.data();
      });
    }

    return backupData;
  };

  const handleBackup = async () => {
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const backupData = await fetchAllCollections();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const dataStr = JSON.stringify(backupData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `masala-cottage-backup-${timestamp}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setMessage({
        type: 'success',
        text: 'Backup created successfully!'
      });
    } catch (error) {
      console.error('Backup failed:', error);
      setMessage({
        type: 'error',
        text: 'Failed to create backup. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Validate data structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid backup file format');
      }

      // Restore data to Firestore
      for (const [collectionName, documents] of Object.entries(data)) {
        for (const [docId, docData] of Object.entries(documents)) {
          await setDoc(doc(db, collectionName, docId), docData, { merge: true });
        }
      }

      setMessage({
        type: 'success',
        text: 'Data restored successfully!'
      });
      event.target.value = ''; // Reset file input
    } catch (error) {
      console.error('Restore failed:', error);
      setMessage({
        type: 'error',
        text: 'Failed to restore data. Please check the backup file and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Database Backup & Restore</h2>
        
        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            <FontAwesomeIcon 
              icon={message.type === 'success' ? faCheckCircle : faExclamationTriangle} 
              className="mr-2" 
            />
            {message.text}
          </div>
        )}

        <div className="space-y-8">
          {/* Backup Section */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create Backup</h3>
            <p className="text-gray-600 mb-4">
              Download a complete backup of all database collections. This includes all content, submissions, and settings.
            </p>
            <button
              onClick={handleBackup}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  Creating Backup...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Download Backup
                </>
              )}
            </button>
          </div>

          {/* Restore Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Restore Data</h3>
            <p className="text-gray-600 mb-4">
              Restore data from a previous backup file. This will merge with existing data.
            </p>
            <div className="mt-2">
              <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer">
                <FontAwesomeIcon icon={faUpload} className="mr-2" />
                Select Backup File
                <input
                  type="file"
                  accept=".json"
                  onChange={handleRestore}
                  disabled={isLoading}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataBackup; 